import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The most frustrating debugging experience I've had all year",
  "description": "",
  "published": "2018-08-28T00:00:00.000Z",
  "redirect_from": ["/blog/frustrating-debugging-journey-ive-year/swizec/8588"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/email-debugging.gif",
        "alt": null
      }}></img></p>
    <p>{`Building emails sucks.`}</p>
    <p>{`Last week I was tasked with a project. `}<em parentName="p">{`"We're rebranding, our emails were built 3 years ago and they look janky. Let's rebuild!"`}</em></p>
    <p><em parentName="p">{`"OK I can do that. But emails suck so I'm def not building that shit from scratch"`}</em></p>
    <p>{`Our designer built an email template in `}<a parentName="p" {...{
        "href": "https://beefree.io/bee-pro/"
      }}>{`BeePro`}</a>{`. Visual email builder, exports to HTML, promises that everything will work on all email clients, on both web and mobile, and look good no matter what you do.`}</p>
    <p>{`Wonderful. Look at us being a responsible team and avoiding all the hard finicky stuff.`}</p>
    <p>{`I look at the template and think `}<em parentName="p">{`"Ok, central body area, some optional pre_body stuff, some optional post_body stuff. Body made out of a few basic widgets."`}</em></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/CleanShot-2018-08-28-at-09.27.06@2x-772x1024.png",
        "alt": null
      }}></img></p>
    <p>{`To avoid bloating up our codebase and make it easier to build many different emails that look just like this, I cut it up into a few Ruby on Rails partials. A basic layout for the email with hooks for pre and post body stuff, a header partial, a title partial, an information row partial, stuff like that.`}</p>
    <p>{`All my partials were built through careful copy and paste. Inspect the exported HTML, cut out a relevant section, fill dynamic info with erb. Easy peasy.`}</p>
    <p>{`Here's what the emails looked like in my test environment. Opening as a browser tab instead of sending via SMTP. 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/kapture_2018-08-24_at_14.37.34.gif",
        "alt": null
      }}></img></p>
    <p>{`And here's what that same email looked like in GMail when my QA team tried it out.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/kapture_2018-08-24_at_14.34.37.gif",
        "alt": null
      }}></img></p>
    <p>{`...`}</p>
    <p>{`Ok, breathe, zennnnn. We can fix this.`}</p>
    <p>{`The problem is that BeePro builds weird HTML that I had to tweak a little and that email clients take out some of your CSS. Sometimes they tweak HTML to make it more correcter.`}</p>
    <p>{`You see BeePro doesn't think in boxes like you and I do. It thinks in full width divs and fakes containers.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/CleanShot-2018-08-28-at-09.33.17@2x-994x1024.png",
        "alt": "BeePro fakes containers"
      }}></img></p>
    <p>{`Tweaked a few things, moved some CSS around, copy pastad some more HTML. Fixed the email yay!`}</p>
    <p>{`Looks great in GMail on the desktop. Surely it's fine everywhere.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/image_from_ios-576x1024.png",
        "alt": null
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "/e1606f4b831844ab17de7fdb0ca1ccb4/media-IYIlvuWc21U4g-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Now here's the thing, you can't inspect elements in the GMail app on your phone. You can't even tell what's rendering, why things are doing what they're doing, or where any of the elements are.`}</p>
    <p>{`A-ha! I'll open GMail on my computer, set my viewport to iPhone size, and debug that way! Modern web development tools to the rescue. Huzzah 💪`}</p>
    <p><img parentName="p" {...{
        "src": "",
        "alt": "GMail is too smart"
      }}></img></p>
    <p>{`GMail is too smart`}</p>
    <p>{`Nope. GMail sees it's running in web mode and scales down your email instead of forcing it to reflow. I don't know how, but it does. Email looks like the desktop version, just smaller.`}</p>
    <p>{`Luckily I'm an old skool web developer. We didn't have tools when I started. I know how it goes!`}</p>
    <p>{`Back when PHP scripts returned a blank screen, if you had an error. Because showing errors on the web leads to security issues, you see. Back when browsers didn't come with developer tools until someone built FireBug for FireFox.`}</p>
    <p>{`Fun times.`}</p>
    <p>{`Those arcane skills came useful once more.`}</p>
    <p>{`In a nutshell 👇`}</p>
    <ol>
      <li parentName="ol">{`Guess which element is a problem`}</li>
      <li parentName="ol">{`Add a border`}</li>
      <li parentName="ol">{`Send email`}</li>
      <li parentName="ol">{`Check on phone`}</li>
      <li parentName="ol">{`Refine guess, goto 1`}</li>
    </ol>
    <p><inlineCode parentName="p">{`1px solid red`}</inlineCode>{` is always my first try. Then I move on to blue and green and dotted and dashed and all sorts of things until I understand how things are rendering and why. Each time you tweak some CSS, some HTML, and move things around.`}</p>
    <p>{`When you're inspecting paddings and margins, you opt for pink and blue and white backgrounds. Background for the big div, background for the small div, hey presto margins and paddings show up. yay`}</p>
    <p>{`Sometimes you take things out to see if they're pushing stuff around. Binary search through the HTML tree to see which line is causing problems.`}</p>
    <p>{`It took me 57 iterations. Here's a gif. Enjoy 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/email-debugging.gif",
        "alt": null
      }}></img></p>
    <p>{`I may have lost my sanity, I may have begged on twitter for a bullet to the head, but by joe I got it done. The email is damn well perfect.`}</p>
    <p>{`Now watch as I walk into work this morning and QA says it doesn't work.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      